import { useState } from 'react'

import { ru, springLength } from '../../lib/physics'
import { Arrow, ArrowDefs, Choice, Slider, зигзаг } from './common'

/**
 * Длина или удлинение (урок 35): ученик растягивает пружину, записывает точки и сравнивает
 * график длины l(F) — он начинается не с нуля — с графиком удлинения Δl(F). Затем — задача
 * урока, где эту разницу и путают.
 */

const L0 = 0.16 // м — как в задаче урока
const K = 200 // Н/м: 40 Н растягивают пружину с 16 до 36 см
const PX_PER_CM = 5
const ВЕРХ = 20

interface Точка {
  f: number
  l: number
}

export default function LengthLab() {
  const [f, setF] = useState(20)
  const [точки, setТочки] = useState<Точка[]>([])
  const [график, setГрафик] = useState<'длина' | 'удлинение'>('длина')
  const [ответ, setОтвет] = useState('')
  const [проверка, setПроверка] = useState<string | null>(null)

  const l = springLength(L0, K, f)
  const lPx = l * 100 * PX_PER_CM
  const записать = () => setТочки((x) => [...x.filter((p) => p.f !== f), { f, l }].sort((a, b) => a.f - b.f))

  const проверить = () => {
    const x = Number(ответ.trim().replace(',', '.'))
    if (!Number.isFinite(x) || ответ.trim() === '') {
      setПроверка('Введи число в ньютонах.')
    } else if (Math.abs(x - 24) < 0.5) {
      setПроверка('✅ Верно: удлинение под 40 Н — 20 см, значит k = 200 Н/м, и 12 см дают 24 Н.')
    } else if (Math.abs(x - (40 * 12) / 36) < 0.5) {
      setПроверка('Это ловушка: 36 см — длина пружины, а не удлинение. Удлинение = 36 − 16 = 20 см.')
    } else {
      setПроверка('Не сходится. Найди сначала удлинение под 40 Н, потом жёсткость k = F / Δl.')
    }
  }

  // график: F по горизонтали 0…40 Н, по вертикали l или Δl 0…40 см
  const gw = 300
  const gh = 200
  const gx = (ff: number) => 40 + (ff / 40) * (gw - 55)
  const gy = (см: number) => gh - 25 - (см / 40) * (gh - 40)
  const значение = (p: Точка) => (график === 'длина' ? p.l : p.l - L0) * 100

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" viewBox="0 0 160 260" role="img" aria-label="Пружина и линейка">
          <ArrowDefs />
          <line x1={20} y1={ВЕРХ - 6} x2={100} y2={ВЕРХ - 6} className="ink-muted" strokeWidth={6} />
          <polyline points={зигзаг(60, ВЕРХ - 3, lPx - 6, 12, 8)} className="ink-band" strokeWidth={2} fill="none" />
          <circle cx={60} cy={ВЕРХ - 3 + lPx} r={4} className="fill-band" />
          <Arrow x1={60} y1={ВЕРХ + lPx + 2} x2={60} y2={ВЕРХ + lPx + 2 + f * 0.8} kind="heavy" label={`${f} Н`} labelDx={6} labelDy={-4} />
          <line x1={110} y1={ВЕРХ - 3} x2={110} y2={ВЕРХ - 3 + 40 * PX_PER_CM} className="ink" />
          {Array.from({ length: 9 }, (_, i) => i * 5).map((см) => (
            <g key={см}>
              <line x1={110} y1={ВЕРХ - 3 + см * PX_PER_CM} x2={118} y2={ВЕРХ - 3 + см * PX_PER_CM} className="ink" />
              <text x={122} y={ВЕРХ + см * PX_PER_CM} className="lab-tick">{см}</text>
            </g>
          ))}
          <text x={122} y={ВЕРХ - 8} className="lab-tick">см</text>
          <line x1={70} y1={ВЕРХ - 3 + L0 * 100 * PX_PER_CM} x2={110} y2={ВЕРХ - 3 + L0 * 100 * PX_PER_CM} className="ink-muted" strokeDasharray="3 3" />
          <text x={72} y={ВЕРХ - 6 + L0 * 100 * PX_PER_CM} className="lab-tick">l₀</text>
        </svg>

        <div>
          <svg className="lab-svg" viewBox={`0 0 ${gw} ${gh}`} role="img" aria-label="График длины или удлинения пружины от силы">
            <line x1={40} y1={gh - 25} x2={gw - 5} y2={gh - 25} className="ink" />
            <line x1={40} y1={10} x2={40} y2={gh - 25} className="ink" />
            {[0, 10, 20, 30, 40].map((ff) => (
              <text key={ff} x={gx(ff)} y={gh - 10} textAnchor="middle" className="lab-tick">{ff}</text>
            ))}
            {[0, 10, 20, 30, 40].map((см) => (
              <text key={см} x={34} y={gy(см) + 3} textAnchor="end" className="lab-tick">{см}</text>
            ))}
            <text x={gw - 5} y={gh - 28} textAnchor="end" className="lab-tick">F, Н</text>
            <text x={44} y={16} className="lab-tick">{график === 'длина' ? 'l, см' : 'Δl, см'}</text>
            {точки.length >= 2 && (
              <polyline points={точки.map((p) => `${gx(p.f)},${gy(значение(p))}`).join(' ')} className="ink-el" fill="none" strokeWidth={1.5} />
            )}
            {точки.map((p) => (
              <circle key={p.f} cx={gx(p.f)} cy={gy(значение(p))} r={4} className="fill-el" />
            ))}
          </svg>
          <Choice
            options={[
              { value: 'длина', label: 'длина l(F)' },
              { value: 'удлинение', label: 'удлинение Δl(F)' },
            ]}
            value={график}
            onChange={setГрафик}
          />
          <div className="lab-caption">
            {график === 'длина'
              ? 'График длины начинается с 16 см, а не с нуля: это длина пружины без груза.'
              : 'График удлинения начинается с нуля: закон Гука F = k·Δl — про удлинение.'}
          </div>
        </div>
      </div>

      <div className="lab-readout">
        <span>длина l = {ru(l * 100, 0)} см</span>
        <span>удлинение Δl = l − l₀ = {ru((l - L0) * 100, 0)} см</span>
      </div>
      <Slider label="Сила" value={f} min={0} max={40} step={5} unit="Н" onChange={setF} />
      <div className="row-wrap" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={записать}>📍 Записать точку</button>
        <button className="btn" onClick={() => setТочки([])}>Очистить график</button>
      </div>

      <div className="lab-predict">
        <div className="lab-predict-q">
          🧮 Задача: пружина длиной 16 см под силой 40 Н стала длиной 36 см. Какая сила растянет её на 12 см?
        </div>
        <div className="row-wrap">
          <input className="lab-input" inputMode="decimal" value={ответ} placeholder="ответ, Н" onChange={(e) => setОтвет(e.target.value)} />
          <button className="btn" onClick={проверить}>Проверить</button>
        </div>
        {проверка && <div className={проверка.startsWith('✅') ? 'lab-verdict ok' : 'lab-verdict'}>{проверка}</div>}
      </div>
    </div>
  )
}
